import { z } from 'zod';
import { BARCODE_MAX_LENGTH, cleanBarcode, isValidBarcode } from './barcode.js';
import { parseDecimal } from './decimal.js';

// ---------- Barcode ----------

export const barcodeValueSchema = z
  .string()
  .transform(cleanBarcode)
  .refine(isValidBarcode, `Barcode ต้องเป็นตัวอักษรอังกฤษ/ตัวเลข 1–${BARCODE_MAX_LENGTH} ตัว`);

export const BARCODE_TYPES = ['FACTORY', 'INTERNAL'] as const;
export type BarcodeType = (typeof BARCODE_TYPES)[number];

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => v || null);

export const createBarcodeSchema = z.object({
  barcode: barcodeValueSchema,
  item_code: z.string().trim().min(1).max(50),
  uom_code: z.string().trim().min(1).max(100),
  barcode_type: z.enum(BARCODE_TYPES).default('FACTORY'),
  supplier: optText(150),
  manufacturer: optText(150),
  /** set when binding from the counting screen (required for Counters) */
  session_id: z.string().uuid().optional(),
});

export const updateBarcodeSchema = z
  .object({
    barcode_type: z.enum(BARCODE_TYPES).optional(),
    supplier: optText(150),
    manufacturer: optText(150),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'ไม่มีข้อมูลที่จะแก้ไข');

export const reasonSchema = z.string().trim().min(3, 'กรุณาระบุเหตุผล (อย่างน้อย 3 ตัวอักษร)').max(500);

export const remapBarcodeSchema = z.object({
  item_code: z.string().trim().min(1).max(50),
  uom_code: z.string().trim().min(1).max(100),
  reason: reasonSchema,
});

// ---------- Session ----------

export const SESSION_STATUSES = ['OPEN', 'REVIEW', 'COMPLETED', 'CANCELLED'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const createSessionSchema = z.object({
  warehouse_code: z.string().trim().toUpperCase().min(1).max(20),
  note: z.string().trim().max(500).optional(),
});

export const completeSessionSchema = z.object({
  override: z.boolean().optional(),
  override_reason: z.string().trim().max(500).optional(),
});

// ---------- Counting entry ----------

const qtyString = z
  .string()
  .trim()
  .refine((s) => parseDecimal(s, 4) !== null, 'จำนวนต้องเป็นตัวเลข ≥ 0 ทศนิยมไม่เกิน 4 ตำแหน่ง');
const factorString = z
  .string()
  .trim()
  .refine((s) => {
    const v = parseDecimal(s, 8);
    return v !== null && v > 0n;
  }, 'factor ต้องมากกว่า 0 ทศนิยมไม่เกิน 8 ตำแหน่ง');

export const ENTRY_METHODS = ['SCAN', 'SEARCH'] as const;

/**
 * Can this item be counted? (Requirements v2.2) Only inventory items. Items that are Inactive in
 * SAP CAN be counted — the SAP status may be wrong while the goods are on the shelf — but the
 * scan screen warns and every entry keeps an `item_inactive` snapshot.
 */
export const isCountable = (i: { is_inventory_item: boolean }) => i.is_inventory_item;

/** One counting entry as created on the device (Requirements §7.2). `id` is the idempotency key. */
export const entryInputSchema = z
  .object({
    id: z.string().uuid(),
    session_id: z.string().uuid(),
    item_code: z.string().trim().min(1).max(50),
    uom_code: z.string().trim().min(1).max(100),
    barcode: z.string().max(BARCODE_MAX_LENGTH).nullable().optional(),
    unbound_barcode: z.string().max(BARCODE_MAX_LENGTH).nullable().optional(),
    entry_method: z.enum(ENTRY_METHODS),
    original_qty: qtyString,
    conversion_factor: factorString.nullable(),
    converted_qty: qtyString.nullable(),
    inventory_uom: z.string().trim().min(1).max(100),
    conversion_status: z.enum(['OK', 'PENDING']),
    replaces_entry_id: z.string().uuid().nullable().optional(),
    /** item was Inactive in SAP on the device at counting time (v2.2); absent from older app versions */
    item_inactive: z.boolean().optional(),
    counted_at: z.string().datetime({ offset: true }),
  })
  .refine((e) => (e.conversion_status === 'OK') === (e.conversion_factor !== null && e.converted_qty !== null), {
    message: 'conversion_status ไม่ตรงกับ factor/converted_qty',
  });
export type EntryInput = z.infer<typeof entryInputSchema>;

export const syncEntriesSchema = z.object({
  device_id: z.string().uuid(),
  device_label: z.string().trim().max(100).optional(),
  entries: z.array(z.unknown()).min(1).max(100),
});

export const voidOpSchema = z.object({
  op_id: z.string().uuid(),
  type: z.literal('VOID'),
  entry_id: z.string().uuid(),
  reason: reasonSchema,
  created_at: z.string().datetime({ offset: true }),
});
export type VoidOp = z.infer<typeof voidOpSchema>;

export const syncOperationsSchema = z.object({
  device_id: z.string().uuid(),
  operations: z.array(z.unknown()).min(1).max(100),
});

export const voidEntrySchema = z.object({ op_id: z.string().uuid(), reason: reasonSchema });

/** Counter may void own entries within this window while the session is OPEN (Requirements §7.4). */
export const COUNTER_VOID_WINDOW_MINUTES = 10;

export type SyncStatus = 'ACCEPTED' | 'DUPLICATE' | 'REJECTED';
export interface SyncResult {
  id: string;
  status: SyncStatus;
  error_code?: string;
  message?: string;
  flags?: string[];
}
